import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';
import { Tile } from '../components/Tile';
import { duration } from '../lib/format';

interface Summary {
  enrolled: number;
  inProgress: number;
  completed: number;
  lessonsCompleted: number;
  learningHours: number;
  averagePercent: number;
}

interface ContinueItem {
  id: string;
  course: {
    id: string;
    title: string;
    slug: string;
    shortDescription: string;
    durationMinutes: number;
    category: { name: string };
  };
  progress: { percent: number; lessonCompleted: number; lessonTotal: number };
  resume: { lessonId: string; title: string } | null;
}

/** Shortcuts for staff roles, each shown only to those who can open it. */
const WORKSPACE: Array<{
  to: string;
  label: string;
  icon: string;
  text: string;
  permission: string;
  /** SkillXzen's own team only; see NavItem.platformOnly in AppShell. */
  platformOnly?: boolean;
}> = [
  {
    to: '/admin/courses',
    label: 'Courses',
    icon: '▦',
    text: 'Build and publish courses.',
    permission: 'COURSE_UPDATE',
  },
  {
    to: '/admin/content',
    label: 'Content Library',
    icon: '❏',
    text: 'Upload and manage lesson content.',
    permission: 'CONTENT_VIEW',
  },
  {
    to: '/admin/review',
    label: 'Review Queue',
    icon: '✓',
    text: 'Approve content and learner reviews.',
    permission: 'CONTENT_APPROVE',
  },
  {
    to: '/admin/users',
    label: 'All Learners & Staff',
    icon: '◍',
    text: 'Everyone who can sign in, across every client.',
    permission: 'USER_VIEW',
    platformOnly: true,
  },
  {
    to: '/admin/customers',
    label: 'Clients',
    icon: '⬢',
    text: 'The companies that use SkillXzen.',
    permission: 'CUSTOMER_VIEW',
    platformOnly: true,
  },
  {
    to: '/admin/reports',
    label: 'Reports & Analytics',
    icon: '◫',
    text: 'Platform figures and exports.',
    permission: 'PLATFORM_REPORT_VIEW',
  },
  {
    to: '/admin/promo-codes',
    label: 'Promo Codes',
    icon: '⌗',
    text: 'Codes, campaigns and their reports.',
    permission: 'COUPON_VIEW',
  },
  {
    to: '/admin/site',
    label: 'Site Content',
    icon: '◨',
    text: 'Banners, pages and FAQs.',
    permission: 'BANNER_MANAGE',
  },
  {
    to: '/admin/ai',
    label: 'AI Assistant',
    icon: '✨',
    text: 'What Vagdevi knows, and how she answers.',
    permission: 'AI_KNOWLEDGE_VIEW',
  },
];

export function Dashboard() {
  const [certificates, setCertificates] = useState<number | null>(null);

  const { user, can } = useAuth();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [continuing, setContinuing] = useState<ContinueItem[] | null>(null);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // Staff roles (content, finance, review…) have no enrolments of their own.
  // Asking for them only collected refusals and drew empty learner tiles, so
  // those roles get their workspace instead.
  const learner = can('ENROLLMENT_VIEW');
  const workspace = WORKSPACE.filter(
    (item) => can(item.permission) && (!item.platformOnly || user?.tenantType === 'PLATFORM'),
  );

  useEffect(() => {
    if (!can('CERTIFICATE_VIEW_OWN')) return;
    let cancelled = false;
    api<{ items: unknown[] }>('/my/certificates')
      .then((result) => {
        if (!cancelled) setCertificates(result.items.length);
      })
      .catch(() => {
        // The tile falls back to a dash; the rest of the dashboard stands.
      });
    return () => {
      cancelled = true;
    };
  }, [can]);

  useEffect(() => {
    if (!learner) return;
    // A learner with no permission for these simply sees the empty state; the
    // dashboard is not the place to surface an authorization error.
    void api<Summary>('/my/progress')
      .then(setSummary)
      .catch(() => setSummary(null));
    void api<{ items: ContinueItem[] }>('/my/continue')
      .then((response) => setContinuing(response.items))
      .catch(() => setContinuing([]));
  }, [learner]);

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">
          {greeting}, {user?.firstName}
        </h1>
        <p className="page__lede">
          {user?.tenantName} · signed in as {user?.roles.join(', ')}
        </p>
      </div>

      {learner ? (
        <>
          <div className="tiles">
            <Tile
              tone="azure"
              icon="▶"
              label="Courses in progress"
              to="/my/courses?filter=in-progress"
              value={summary ? String(summary.inProgress) : '—'}
              note={
                summary && summary.enrolled > 0
                  ? `${summary.enrolled} enrolled in total`
                  : undefined
              }
            />
            <Tile
              tone="teal"
              icon="✓"
              label="Completed"
              to="/my/courses?filter=completed"
              value={summary ? String(summary.completed) : '—'}
              note={summary ? `${summary.lessonsCompleted} lessons finished` : undefined}
            />
            <Tile
              tone="gold"
              icon="✦"
              label="Certificates"
              to="/my/certificates"
              value={certificates === null ? '—' : String(certificates)}
              note={certificates ? 'Earned and verifiable' : 'Finish a course to earn one'}
            />
            <Tile
              tone="indigo"
              icon="◷"
              label="Learning hours"
              to="/my/courses"
              value={summary ? `${summary.learningHours}` : '—'}
              // The bar is course progress, not hours, so the note has to say so.
              note={
                summary && summary.averagePercent > 0
                  ? `Average course progress: ${summary.averagePercent}%`
                  : undefined
              }
              progress={summary?.averagePercent}
            />
          </div>

          <section className="section">
            <h2 className="section__title">Continue learning</h2>
            {continuing === null ? (
              <div className="skel" style={{ height: 120 }} />
            ) : continuing.length > 0 ? (
              <div className="grid-cards">
                {continuing.map((item) => {
                  const href = item.resume
                    ? `/learn/${item.course.id}?lesson=${item.resume.lessonId}`
                    : `/learn/${item.course.id}`;
                  return (
                    <article className="lcard lcard--link" key={item.id}>
                      <div className="lcard__head">
                        <span className="ccard__cat">{item.course.category.name}</span>
                        <span className="pill pill--pending">{item.progress.percent}%</span>
                      </div>
                      <h3 className="lcard__title">
                        {/* Covers the whole card (see .lcard--link), so the card
                          is clickable anywhere, not only on its button. */}
                        <Link className="lcard__link" to={href}>
                          {item.course.title}
                        </Link>
                      </h3>
                      {item.resume ? (
                        <p className="lcard__desc">Next up — {item.resume.title}</p>
                      ) : (
                        <p className="lcard__desc">{item.course.shortDescription}</p>
                      )}
                      <div className="bar" aria-hidden="true">
                        <i style={{ width: `${item.progress.percent}%` }} />
                      </div>
                      <p className="lcard__meta">
                        {item.progress.lessonCompleted} of {item.progress.lessonTotal} lessons ·{' '}
                        {duration(item.course.durationMinutes)}
                      </p>
                      <Link
                        className="btn btn--sm btn--block"
                        to={href}
                        // The title link already announces this card; the
                        // button is the visible affordance, not a second stop.
                        tabIndex={-1}
                        aria-hidden="true"
                      >
                        {/* Worded as My Learning words it: nothing to resume yet. */}
                        {item.progress.percent === 0 ? 'Start course' : 'Resume'}
                      </Link>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="card">
                <div className="state">
                  <div className="state__art" aria-hidden="true">
                    ◎
                  </div>
                  <h4>Nothing in progress</h4>
                  <p>Enrol in a course and it will appear here, ready to pick up.</p>
                  <Link className="btn btn--sm" to="/catalog">
                    Browse the catalog
                  </Link>
                </div>
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="section">
          <h2 className="section__title">Your workspace</h2>
          {workspace.length > 0 ? (
            <div className="workspace">
              {workspace.map((item) => (
                <Link className="workspace__card" to={item.to} key={item.to}>
                  <span className="workspace__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span>
                    <strong>{item.label}</strong>
                    <span className="workspace__text">{item.text}</span>
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="card">
              <div className="state">
                <h4>Nothing assigned to your role yet</h4>
                <p>Browse the catalog, or ask an administrator for the access you need.</p>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}
